import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { X, AlertTriangle, Package, User, Calendar, Building2 } from "lucide-react";
import moment from "moment";
import {
  INCIDENT_SEVERITY,
  INCIDENT_STATUS,
  INCIDENT_TYPE_LABEL,
  DEPARTMENT_LABELS,
} from "../../constants/incidents";
import { getPackageIncident } from "../../api/packages";
import { showToast } from "../../utils/showToast";
import { Modal } from "../ui/Modal";
import SacTab from "./SacTab";
import AdministrationTab from "./AdministrationTab";
import SupervisionTab from "./SupervisionTab";
import AppealTab from "./AppealTab";
import { CopyButton } from "../ui/CopyButton";
import "./IncidentDetail.scss";

const TABS = [
  { key: "sac", label: "SAC" },
  { key: "administration", label: "Administración" },
  { key: "supervision", label: "Supervisión" },
];

function getDeptBadgeClass(incident, dept) {
  const deptData = incident[dept];
  const isRequired = deptData?.required === true ||
    (dept === "sac" && !incident.requiredDepartments); // legacy: sac always required
  const isResolved = deptData?.resolved === true ||
    (dept === "sac" && !!incident.resolvedAt && !incident.requiredDepartments);

  if (!isRequired) return "optional";
  if (isResolved) return "resolved";
  return "pending";
}

export default function IncidentDetailModal({ incidentId, onClose, onUpdated }) {
  const { t } = useTranslation();
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("sac");

  // `silent`: recarga sin volver al skeleton. Si no, el refresco que pide
  // AppealTab tras resolver lo desmonta y se pierde su aviso en línea.
  const loadIncident = useCallback(async (id, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await getPackageIncident(id);
      if (response.success && response.data) {
        setIncident(response.data);
      } else {
        showToast(t("Error al cargar la incidencia"), false);
      }
    } catch {
      showToast(t("Error al cargar la incidencia"), false);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (incidentId) loadIncident(incidentId);
  }, [incidentId, loadIncident]);

  // Esc/backdrop/body-scroll-lock ahora los maneja `ui/Modal`.

  const handleUpdated = (updatedIncident) => {
    if (updatedIncident) {
      setIncident(updatedIncident);
    } else {
      loadIncident(incidentId, { silent: true });
    }
    onUpdated && onUpdated();
  };

  const incidentStatus = incident?.status || (incident?.resolvedAt ? "cerrada" : "abierta");
  const requiredDepts = incident?.requiredDepartments || ["sac"];

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      hideClose
      ariaLabel={t("Detalle de la incidencia")}
      className="incident-modal"
      bodyClassName="incident-modal__scroller"
    >
      <div className="incident-modal__inner">

        {/* ── Header ── */}
        <div className="incident-modal__header">
          <div className="incident-modal__header-left">
            <h3 className="incident-modal__title">
              <AlertTriangle size={20} />
              {t("Detalle de la incidencia")}
            </h3>
            {!loading && incident && (
              <div className="incident-modal__status-row">
                <span className={`incident-badge incident-badge--${incidentStatus}`}>
                  {t(INCIDENT_STATUS[incidentStatus] || incidentStatus)}
                </span>
                {incident.severity && (
                  <span className={`incident-badge incident-badge--${incident.severity}`}>
                    {t(INCIDENT_SEVERITY[incident.severity] || incident.severity)}
                  </span>
                )}
                {incident.incidentType && (
                  <span className="incident-badge incident-badge--type">
                    {t(INCIDENT_TYPE_LABEL[incident.incidentType] || incident.incidentType)}
                  </span>
                )}
              </div>
            )}
          </div>
          <button className="incident-modal__close" onClick={onClose} aria-label={t("Cerrar")}>
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div className="incident-modal__body">
            <div className="incident-skeleton">
              <div className="incident-skeleton__line incident-skeleton__line--title" />
              <div className="incident-skeleton__line" />
              <div className="incident-skeleton__line incident-skeleton__line--short" />
              <div className="incident-skeleton__line" />
            </div>
          </div>
        ) : incident ? (
          <>
            {/* ── Info general ── */}
            <div className="incident-modal__info">
              {/* Creado por */}
              <div className="incident-modal__info-card">
                <div className="incident-modal__info-icon"><User size={16} /></div>
                <div className="incident-modal__info-content">
                  <span className="incident-modal__info-label">{t("Creado por")}</span>
                  <span className="incident-modal__info-value">
                    {incident.createdBy?.name
                      ? `${incident.createdBy.name}${incident.createdBy.lastName ? ` ${incident.createdBy.lastName}` : ""}`
                      : incident.createdByUser?.name
                        ? `${incident.createdByUser.name}${incident.createdByUser.lastName ? ` ${incident.createdByUser.lastName}` : ""}`
                        : "—"}
                  </span>
                </div>
              </div>

              {/* Fecha */}
              <div className="incident-modal__info-card">
                <div className="incident-modal__info-icon"><Calendar size={16} /></div>
                <div className="incident-modal__info-content">
                  <span className="incident-modal__info-label">{t("Fecha de creación")}</span>
                  <span className="incident-modal__info-value">
                    {moment(incident.createdAt).format("DD/MM/YYYY HH:mm")}
                  </span>
                </div>
              </div>

              {/* N° seguimiento */}
              {(incident.package?.followingNumber || incident.packageId) && (
                <div className="incident-modal__info-card">
                  <div className="incident-modal__info-icon"><Package size={16} /></div>
                  <div className="incident-modal__info-content">
                    <span className="incident-modal__info-label">{t("N° seguimiento")}</span>
                    <span className="incident-modal__info-value inline-flex items-center gap-0.5">
                      {incident.package?.followingNumber || "—"}
                      <CopyButton
                        value={incident.package?.followingNumber}
                        label={t("Copiar número de seguimiento")}
                        copiedMessage={t("Número de seguimiento copiado")}
                      />
                    </span>
                  </div>
                </div>
              )}

              {/* Dueño del paquete */}
              {incident.package?.senderInfo?.businessName && (
                <div className="incident-modal__info-card">
                  <div className="incident-modal__info-icon"><Building2 size={16} /></div>
                  <div className="incident-modal__info-content">
                    <span className="incident-modal__info-label">{t("Empresa")}</span>
                    <span className="incident-modal__info-value">
                      {incident.package.senderInfo.businessName}
                    </span>
                  </div>
                </div>
              )}

              {/* Asignado a */}
              {(incident.assignedUser?.name || incident.package?.assignTo?.name) && (
                <div className="incident-modal__info-card">
                  <div className="incident-modal__info-icon"><User size={16} /></div>
                  <div className="incident-modal__info-content">
                    <span className="incident-modal__info-label">{t("Asignado a")}</span>
                    <span className="incident-modal__info-value">
                      {incident.assignedUser?.name
                        ? `${incident.assignedUser.name}${incident.assignedUser.lastName ? ` ${incident.assignedUser.lastName}` : ""}`
                        : incident.package?.assignTo?.name || "—"}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {incident.appeal ? (
              /* ── Apelación: panel dedicado, sin barra de pestañas ── */
              <div className="incident-modal__body">
                <AppealTab
                  incident={incident}
                  onUpdated={handleUpdated}
                  readOnly={["approved", "rejected"].includes(incident.appeal.status)}
                />
              </div>
            ) : (
              <>
                {/* ── Tabs ── */}
                <div className="incident-modal__tabs">
                  {TABS.map(({ key, label }) => {
                    const badgeType = getDeptBadgeClass(incident, key);
                    const isTabRequired = requiredDepts.includes(key) ||
                      (key === "sac" && !incident.requiredDepartments);

                    return (
                      <button
                        key={key}
                        className={`incident-modal__tab${activeTab === key ? " incident-modal__tab--active" : ""}`}
                        onClick={() => setActiveTab(key)}
                      >
                        {t(DEPARTMENT_LABELS[key] || label)}
                        {isTabRequired && (
                          <span className={`incident-modal__tab-badge incident-modal__tab-badge--${badgeType}`}>
                            {badgeType === "resolved" ? "✓" : badgeType === "pending" ? "!" : "—"}
                          </span>
                        )}
                        {!isTabRequired && (
                          <span className="incident-modal__tab-badge incident-modal__tab-badge--optional">
                            –
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* ── Contenido del tab activo ── */}
                <div className="incident-modal__body">
                  {activeTab === "sac" && (
                    <SacTab
                      incident={incident}
                      onUpdated={handleUpdated}
                      readOnly={incidentStatus === "cerrada" && !!incident.sac?.resolved}
                    />
                  )}
                  {activeTab === "administration" && (
                    <AdministrationTab
                      incident={incident}
                      onUpdated={handleUpdated}
                      readOnly={incident.administration?.resolved === true}
                    />
                  )}
                  {activeTab === "supervision" && (
                    <SupervisionTab
                      incident={incident}
                      onUpdated={handleUpdated}
                      readOnly={incident.supervision?.resolved === true}
                    />
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="incident-modal__body" style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-tertiary)" }}>
            {t("No se pudo cargar la incidencia.")}
          </div>
        )}
      </div>
    </Modal>
  );
}
